import { CodeableConceptFragment, ExtensionFragment } from '@/services/graphql';
import { gql } from '@bariendo/lib/graphql';
import { HumanName, Resource } from '@medplum/fhirtypes';

export type OrganizationFragment = {
  organizationName?: string;
};

// conflicting 'name' require to rename to organizationName and patientName
const ORGANIZATION_FRAGMENT = gql`
  fragment OrganizationFragment on Organization {
    organizationName: name
  }
`;

export type PatientFragment = {
  patientName?: HumanName[];
  telecom?: Array<{
    system?: string;
    value?: string;
    use?: string;
  }>;
};

const PATIENT_FRAGMENT = gql`
  fragment PatientFragment on Patient {
    patientName: name {
      given
      family
    }
    telecom {
      system
      value
      use
    }
  }
`;

export type CommunicationFragment = {
  status: string;
  sent?: string;
  identifier?: Array<{ system?: string; value?: string }>;
  subject?: {
    reference: string;
    display?: string;
  };
  payload?: Array<{
    id?: string;
    contentString?: string;
  }>;
  category?: CodeableConceptFragment[];
  medium?: CodeableConceptFragment[];
  about?: Array<{
    reference?: string;
    display?: string;
  }>;
  extension?: ExtensionFragment[];
  sender?: {
    display?: string;
  };
};

// `identifier` and the Twilio call history under `extension` are what
// `deriveVoiceCallDisplayStatus` reads to tell an answered call from a missed
// one. FHIR `status` cannot: a missed call completes like any other.
const COMMUNICATION_FRAGMENT = gql`
  fragment CommunicationFragment on Communication {
    status
    sent
    identifier {
      system
      value
    }
    subject {
      reference
      display
    }
    payload {
      id
      contentString
    }
    category {
      coding {
        code
        display
      }
    }
    medium {
      coding {
        code
        display
      }
    }
    about {
      reference
      display
    }
    extension {
      url
      valueString
      extension {
        url
        valueString
        valueReference {
          reference
        }
        valueAttachment {
          data
        }
      }
    }
    sender {
      display
    }
  }
`;

export type NotificationTask = Resource & {
  resourceType: 'Task';
  id: string;
  status: string;
  intent: string;
  authoredOn?: string;
  owner?: {
    reference: string;
    display?: string;
  };
  groupIdentifier?: {
    value: string;
  };
  focus?: {
    reference: string;
    display?: string;
    resource?: CommunicationFragment;
  };
  for?: {
    reference: string;
    display?: string;
    resource?: OrganizationFragment | PatientFragment;
  };
  description?: string;
  priority?: string;
  executionPeriod?: {
    start?: string;
    end?: string;
  };
  basedOn?: Array<{
    reference?: string;
    display?: string;
  }>;
  requester?: {
    reference?: string;
    display?: string;
  };
};

export const NOTIFICATION_TASKS_QUERY = gql`
  ${ORGANIZATION_FRAGMENT}
  ${PATIENT_FRAGMENT}
  ${COMMUNICATION_FRAGMENT}
  query NotificationTasks(
    $group_identifier: String!
    $owner: String!
    $status: String!
    $_filter: String
    $_sort: String!
  ) {
    TaskList(group_identifier: $group_identifier, owner: $owner, status: $status, _filter: $_filter, _sort: $_sort) {
      resourceType
      id
      status
      intent
      authoredOn
      owner {
        reference
        display
      }
      groupIdentifier {
        value
      }
      focus {
        reference
        display
        resource {
          ...CommunicationFragment
        }
      }
      for {
        reference
        display
        resource {
          ...PatientFragment
          ...OrganizationFragment
        }
      }
      description
      priority
      executionPeriod {
        start
        end
      }
      basedOn {
        reference
        display
      }
      requester {
        reference
        display
      }
    }
  }
`;

/**
 * Role-assigned clinical tasks for the inbox Tasks view: same row shape as
 * NOTIFICATION_TASKS_QUERY but scoped by performer (Task.performerType) —
 * user-independent, so every holder of the role sees the same rows.
 */
export const ROLE_TASKS_QUERY = gql`
  ${ORGANIZATION_FRAGMENT}
  ${PATIENT_FRAGMENT}
  ${COMMUNICATION_FRAGMENT}
  query RoleTasks(
    $group_identifier: String!
    $performer: String!
    $status: String!
    $_filter: String
    $_sort: String!
  ) {
    TaskList(
      group_identifier: $group_identifier
      performer: $performer
      status: $status
      _filter: $_filter
      _sort: $_sort
    ) {
      resourceType
      id
      status
      intent
      authoredOn
      owner {
        reference
        display
      }
      groupIdentifier {
        value
      }
      focus {
        reference
        display
        resource {
          ...CommunicationFragment
        }
      }
      for {
        reference
        display
        resource {
          ...PatientFragment
          ...OrganizationFragment
        }
      }
      description
      priority
      executionPeriod {
        start
        end
      }
      basedOn {
        reference
        display
      }
      requester {
        reference
        display
      }
    }
  }
`;
